import React, { useEffect, useMemo, useState } from "react";
import { all_routes } from "../../../router/all_routes";
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb";
import Sidebar from "../../../../core/common/sidebar/sidebar";
import API_URLS from "../../../../core/common/apiUrls";

/* ===== Types that match your API response ===== */
type ApiCancelPolicy = {
  CancellationCharge: number;   // 100.0
  ChargeType: number;           // 2 (looks like percentage)
  Currency: string;             // "INR"
  FromDate: string;             // "07/11/2025 00:00:00"
  ToDate: string;               // "05/12/2025 23:59:59"
};

type ApiGuest = {
  city: string;
  contact_no: string;
  dob: string;
  email: string;
  first_name: string;
  gender: string; // "Male"
  is_lead: number; // 1/0
  last_name: string;
  nationality: string;
  title: string; // "Mr"
};

type ApiHotelBooking = {
  amount: number;                 // 41947.18
  booking_id: number;             // 65 (your app-level id)
  cancel_policies: ApiCancelPolicy[];
  cancellation_info: any | null;
  cancellation_policy: string;    // long encoded string
  check_in: string;               // "06-12-2025"
  check_out: string;              // "08-12-2025"
  created_at: string;             // "08-11-2025 07:12"
  currency: string;               // "INR"
  guests: ApiGuest[];
  hotel_code: string;             // "1002877"
  hotel_name: string;             // "Pearl Marina Hotel Apartment"
  location: string;               // "WATER FRONT - ... Dubai"
  status: string;                 // "Confirmed"
  tbo_booking_id: number;         // 2036879  <-- used for cancel/status APIs
};

/* ===== Local UI type (normalized) ===== */
type UiHotelBooking = {
  AppBookingId: number;          // booking_id
  TboBookingId: number;          // tbo_booking_id (for API actions)
  HotelName: string;
  City: string;                  // from guests[0].city or parsed from location
  Location: string;
  CheckIn: string;               // ISO string or original for display
  CheckOut: string;
  Nights: number;
  LeadGuest: string;
  GuestsCount: number;
  Amount: number;
  Currency: string;
  Status: string;
  CreatedAt: string;
  CancelPolicies: ApiCancelPolicy[];
  CancellationPolicyText: string;
  ChangeRequestId?: number;      // set after "cancel" action
};

const UserHotelsBooking: React.FC = () => {
  const routes = all_routes;

  // ---------- USER ----------
  const userId = useMemo(() => {
    const sid = sessionStorage.getItem("user_id");
    if (sid) return Number(sid);
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || "null");
      return u?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  // ---------- STATE ----------
  const [list, setList] = useState<UiHotelBooking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);
  const [cancelLoadingId, setCancelLoadingId] = useState<number | null>(null);
  const [policyOpen, setPolicyOpen] = useState<number | null>(null); // toggle policy row

  // ---------- HELPERS ----------
  const parseDDMMYYYY = (s?: string): Date | null => {
    if (!s) return null;
    // supports "06-12-2025" or "06/12/2025"
    const m = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
    if (!m) return null;
    const [_, dd, mm, yyyy] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  };

  const daysBetween = (a: Date | null, b: Date | null) => {
    if (!a || !b) return 0;
    const ms = b.getTime() - a.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  };

  const titleCase = (s?: string) =>
    (s || "").replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.slice(1).toLowerCase());

  const decodePolicy = (raw: string) => {
    // your string looks like: "...#^#100.00% of total amount...|#!#"
    // Quick clean for display:
    return raw
      .replace(/\#\^\#/g, " — ")
      .replace(/\|\#\!\#/g, "")
      .trim();
  };

  const toUi = (row: ApiHotelBooking): UiHotelBooking => {
    const ci = parseDDMMYYYY(row.check_in);
    const co = parseDDMMYYYY(row.check_out);
    const cityByGuest = row.guests?.[0]?.city || "";
    const cityFromLocMatch = (row.location || "").split(",").slice(-1)[0]?.trim() || "";
    const city = cityByGuest || cityFromLocMatch || "";

    const lead = row.guests?.find((g) => Number(g.is_lead) === 1) || row.guests?.[0];
    const leadName = lead ? `${lead.title ? lead.title + " " : ""}${titleCase(lead.first_name)} ${titleCase(lead.last_name)}`.trim() : "—";

    return {
      AppBookingId: row.booking_id,
      TboBookingId: row.tbo_booking_id,
      HotelName: row.hotel_name,
      City: city,
      Location: row.location,
      CheckIn: ci ? ci.toISOString() : row.check_in,
      CheckOut: co ? co.toISOString() : row.check_out,
      Nights: daysBetween(ci, co),
      LeadGuest: leadName,
      GuestsCount: Array.isArray(row.guests) ? row.guests.length : 0,
      Amount: row.amount,
      Currency: row.currency || "",
      Status: row.status || "—",
      CreatedAt: row.created_at || "",
      CancelPolicies: row.cancel_policies || [],
      CancellationPolicyText: row.cancellation_policy ? decodePolicy(row.cancellation_policy) : "",
    };
  };

  // ---------- FETCH ----------
  const fetchBookings = async () => {
    if (!userId) {
      setErr("User not found in session. Please sign in again.");
      setList([]);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(API_URLS.HOTELS.MY_BOOKINGS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      const rows: ApiHotelBooking[] = Array.isArray(data) ? data : (data.bookings || data.data || []);
      const ui = rows.map(toUi);
      setList(ui);
    } catch (e: any) {
      setErr(e?.message || "Failed to load bookings.");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- ACTIONS ----------
  const cancelBooking = async (row: UiHotelBooking) => {
    if (!row?.TboBookingId) return;
    setCancelLoadingId(row.TboBookingId);
    try {
      const res = await fetch(API_URLS.HOTELS.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          BookingId: row.TboBookingId, // <-- your API uses TBO booking id here
          Remarks: "Customer wants cancellation",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setList((prev) =>
          (prev || []).map((b) =>
            b.TboBookingId === row.TboBookingId
              ? { ...b, Status: "Cancellation Requested", ChangeRequestId: data?.ChangeRequestId || b.ChangeRequestId }
              : b
          )
        );
      } else {
        alert(data?.message || data?.error || "Failed to request cancellation.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to request cancellation.");
    } finally {
      setCancelLoadingId(null);
    }
  };

  const checkStatus = async (row: UiHotelBooking) => {
    if (!row?.ChangeRequestId) {
      alert("No ChangeRequestId for this booking.");
      return;
    }
    setStatusLoadingId(row.ChangeRequestId);
    try {
      const res = await fetch(API_URLS.HOTELS.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "status",
          ChangeRequestId: row.ChangeRequestId,
          Remarks: "Customer wants cancellation",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const newStatus = data?.Status || data?.status || data?.message || "Cancellation status fetched";
        setList((prev) =>
          (prev || []).map((b) => (b.TboBookingId === row.TboBookingId ? { ...b, Status: String(newStatus) } : b))
        );
      } else {
        alert(data?.message || data?.error || "Failed to fetch status.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to fetch status.");
    } finally {
      setStatusLoadingId(null);
    }
  };

  // ---------- EFFECT ----------
  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const breadcrumbs = [
    { label: "My Bookings", link: routes.home1, active: false },
    { label: "My Bookings", active: true },
    { label: "Hotels", active: true },
  ];

  return (
    <div>
      <Breadcrumb title="My Bookings" breadcrumbs={breadcrumbs} backgroundClass="breadcrumb-bg-04" />

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            {/* Main */}
            <div className="col-xl-9 col-lg-8 theiaStickySidebar">
              <div className="card booking-header">
                <div className="card-body header-content d-flex align-items-center justify-content-between flex-wrap">
                  <div>
                    <h6>Hotels</h6>
                    <p className="fs-14 text-gray-6 fw-normal">
                      No of Booking : {(list?.length || 0).toString()}
                    </p>
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <button className="btn btn-light" onClick={fetchBookings}>
                      <i className="isax isax-refresh" /> Refresh
                    </button>
                  </div>
                </div>
              </div>

              <div className="card hotel-list">
                <div className="card-body p-0">
                  <div className="list-header d-flex align-items-center justify-content-between flex-wrap">
                    <h6 className="">Booking List</h6>
                  </div>

                  {loading ? (
                    <div className="p-4">Loading…</div>
                  ) : err ? (
                    <div className="p-4 text-danger">{err}</div>
                  ) : (list?.length || 0) === 0 ? (
                    <div className="p-4 text-muted">No bookings found.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr>
                            <th>App ID</th>
                            <th>TBO ID</th>
                            <th>Hotel</th>
                            <th>City</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Nights</th>
                            <th>Lead Guest</th>
                            <th>Guests</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th style={{ width: 260 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list!.map((b) => (
                            <React.Fragment key={b.AppBookingId}>
                              <tr>
                                <td className="fw-semibold">#{b.AppBookingId}</td>
                                <td>{b.TboBookingId}</td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold">{b.HotelName}</span>
                                    <span className="text-muted small">{b.Location}</span>
                                  </div>
                                </td>
                                <td>{b.City || "—"}</td>
                                <td>{b.CheckIn ? new Date(b.CheckIn).toLocaleDateString() : "—"}</td>
                                <td>{b.CheckOut ? new Date(b.CheckOut).toLocaleDateString() : "—"}</td>
                                <td>{b.Nights}</td>
                                <td>{b.LeadGuest}</td>
                                <td>{b.GuestsCount}</td>
                                <td>
                                  {b.Currency || ""} {b.Amount != null ? Number(b.Amount).toFixed(2) : "—"}
                                </td>
                                <td>
                                  <span className="badge badge-outline rounded-pill">
                                    {b.Status || "Confirmed"}
                                  </span>
                                </td>
                                <td>{b.CreatedAt || "—"}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => setPolicyOpen(policyOpen === b.AppBookingId ? null : b.AppBookingId)}
                                    >
                                      Policy
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      disabled={!!cancelLoadingId}
                                      onClick={() => cancelBooking(b)}
                                    >
                                      {cancelLoadingId === b.TboBookingId ? "Cancelling…" : "Cancel"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      disabled={!b.ChangeRequestId || !!statusLoadingId}
                                      onClick={() => checkStatus(b)}
                                    >
                                      {statusLoadingId === b.ChangeRequestId ? "Checking…" : "Status"}
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {/* Expandable cancellation policy row */}
                              {policyOpen === b.AppBookingId && (
                                <tr>
                                  <td colSpan={13} className="bg-light">
                                    <div className="p-3">
                                      <h6 className="mb-2">Cancellation Policy</h6>
                                      {b.CancellationPolicyText ? (
                                        <p className="mb-3">{b.CancellationPolicyText}</p>
                                      ) : (
                                        <p className="text-muted">No policy text available.</p>
                                      )}
                                      {b.CancelPolicies?.length > 0 && (
                                        <div className="table-responsive">
                                          <table className="table table-sm align-middle">
                                            <thead>
                                              <tr>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Charge</th>
                                                <th>Type</th>
                                                <th>Currency</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {b.CancelPolicies.map((p, i) => (
                                                <tr key={i}>
                                                  <td>{p.FromDate}</td>
                                                  <td>{p.ToDate}</td>
                                                  <td>{p.CancellationCharge}</td>
                                                  <td>{p.ChargeType === 2 ? "Percentage" : String(p.ChargeType)}</td>
                                                  <td>{p.Currency}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              {/* /list */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserHotelsBooking;
